import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Pause, Play, Plug, Save, Trash2, Workflow } from "lucide-react";
import { useProcessInstance, useTemplateSpec } from "@/api/queries/processInstances";
import { useCases } from "@/api/queries/cases";
import { useProcess } from "@/api/queries/processes";
import { useAgents } from "@/api/queries/agents";
import { useAgentModels } from "@/api/queries/models";
import { useUsers } from "@/api/queries/users";
import {
  useConfigureInstance,
  useDeleteInstance,
  usePauseInstance,
  useProvisionConnectors,
  useResumeInstance,
  type ConfigureInstancePatch,
} from "@/api/mutations/processInstances";
import { ApiError } from "@/api/client";
import { BackLink, DetailHeader } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EntityLink } from "@/components/EntityLink";
import { CaseStatusBadge, GapRow, InstanceStatusBadge, StepProgress } from "@/components/ProcessBits";
import { ManualStartCard } from "@/components/process-actions/ManualStartCard";
import { RequiredPluginsCard } from "@/components/process-plugins/RequiredPluginsCard";
import { EntscheiderCard } from "@/components/process-entscheider/EntscheiderCard";
// #330 (UC-1): dieselbe pure Schablone/Prüfung, die auch die Freigabe-Karte benutzt.
import { baueEntscheiderPatch, entscheiderFehler } from "@/components/process-entscheider/entscheiderSatz";
import { relativeTime } from "@/lib/relativeTime";
import { costClassName, formatUsd } from "@/lib/formatCost";
// #P001 (UC-2): dieselbe Beschriftung wie `/model` im Chat — eine Formel, zwei Oberflächen.
import { fixLabelAus, mitAltwert, modelBasisAus } from "@/lib/modelTarget";
import { useUserContext, darfVorgang } from "@/auth/operations";
// #326 (UC-2): EIN Wortschatz und EINE Regel-Auslegung für Vorlage und Einrichtung —
// die Regler heissen hier wie dort gleich, und die Grenzen des Masters stehen an einer Stelle.
import {
  ausloeserLabel,
  cronFehler,
  pruefeOverride,
  reglerLabel,
  MAX_PRIMITIVE_LAENGE,
  MAX_PROMPTTEXT_LAENGE,
  MAX_TIMEZONE_LAENGE,
} from "@/components/process-spec/specDocument";
import { StepModelCard } from "@/components/process-step-model/StepModelCard";
// #P002 (UC-3): dieselbe pure Regel, die auch die Karte benutzt.
import { baueStepModelPatch } from "@/components/process-step-model/stepModelSatz";
import type {
  EntscheiderWahl,
  InstanceGateView,
  InstanceStepModelView,
  ProcessInstanceView,
  TemplateSpecView,
} from "@/types";

// #300 (UC-4): Instanz-Konfiguration — Agent binden, Trigger wählen/konfigurieren,
// Input-Vorbelegungen, Settings-Overrides (nur overridable, nur enger). Die Formulare
// rendern aus der AKTIVEN Template-Spec (GET …/template-spec); das Urteil fällt der
// Master (INVALID_INSTANCE_* / Readiness-Gaps).

// In #300 konfigurierbar (README: „noch nicht scharf"); mail/webhook kommen mit #304.
const CONFIGURABLE_KINDS = ["manual", "schedule"] as const;

// #P001 (UC-2): Haus-Stil eines nativen Auswahlfelds — geteilt mit dem Start-Dialog (UC-4).
import { NATIVE_SELECT_CLASS as INPUT_CLASS } from "@/lib/formClasses";

export interface Draft {
  agentId: string;
  triggerKind: string;
  cron: string;
  timezone: string;
  promptText: string;
  model: string;
  inputDefaults: Record<string, string>;
  settingsOverrides: Record<string, string>;
  /**
   * #330 (UC-1): die Entscheider-Wahl — hier stehen NUR die in dieser Sitzung BERÜHRTEN
   * Schritte. Der gespeicherte Stand kommt aus der Instanz (`entscheiderOverrides`) und wird
   * bewusst NICHT hierher kopiert: sonst schriebe jedes Speichern der Regler-Maske nebenbei
   * alle Entscheider zurück (Phantom-Diff).
   */
  entscheider: Record<string, EntscheiderWahl>;
  /**
   * #P002 (UC-3): die Modell-Wahl je Schritt — ebenfalls NUR die BERÜHRTEN Schritte, aus
   * demselben Grund (der Master ersetzt die Sammlung voll).
   */
  stepModels: Record<string, string>;
}

function draftFrom(instance: ProcessInstanceView): Draft {
  const cfg = (instance.trigger?.config ?? {}) as { cron?: string; timezone?: string; promptText?: string };
  return {
    agentId: instance.agentId ?? "",
    triggerKind: instance.trigger?.kind ?? "",
    cron: cfg.cron ?? "",
    timezone: cfg.timezone ?? "Europe/Zurich",
    promptText: cfg.promptText ?? "",
    model: instance.model ?? "",
    inputDefaults: Object.fromEntries(Object.entries(instance.inputDefaults).map(([k, v]) => [k, String(v)])),
    settingsOverrides: Object.fromEntries(Object.entries(instance.settingsOverrides).map(([k, v]) => [k, String(v)])),
    entscheider: {},
    stepModels: {},
  };
}

// Formular-Strings → typisierte Werte (nach Casefield-/Definitions-Typ); leer = weglassen.
// Review LOW-15: eine unlesbare Zahl wird als FEHLER gemeldet, nie still weggelassen.
function typedValue(raw: string, kind: "number" | "boolean" | "string", label: string): string | number | boolean | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (kind === "number") {
    const n = Number(trimmed);
    if (!Number.isFinite(n)) throw new Error(`„${label}" braucht eine Zahl`);
    return n;
  }
  if (kind === "boolean") {
    if (trimmed !== "true" && trimmed !== "false") throw new Error(`„${label}" braucht true/false`);
    return trimmed === "true";
  }
  // #326 (Review L7): der Wire-Deckel eines einzelnen Wertes (`primitiveValue`). Ohne ihn
  // kommt statt einer Feld-Meldung nur ein generisches „Validation failed" zurück.
  if (trimmed.length > MAX_PRIMITIVE_LAENGE) {
    throw new Error(`„${label}" ist länger als ${MAX_PRIMITIVE_LAENGE} Zeichen`);
  }
  return trimmed;
}

function fieldKind(type: string): "number" | "boolean" | "string" {
  if (type === "number" || type === "money") return "number";
  if (type === "boolean") return "boolean";
  return "string";
}

function buildPatch(
  draft: Draft,
  spec: TemplateSpecView,
  gates: InstanceGateView[] | null | undefined,
  gespeichert: Record<string, EntscheiderWahl> | undefined,
  // #P002 (UC-3): dieselbe Mechanik für die Modell-Wahl je Schritt.
  stepZeilen: InstanceStepModelView[] | null | undefined,
  stepGespeichert: Record<string, string> | undefined,
): ConfigureInstancePatch {
  const inputDefaults: Record<string, string | number | boolean> = {};
  const fieldByKey = new Map(spec.casefields.map((f) => [f.key, f] as const));
  for (const key of [...spec.input.required, ...spec.input.optional]) {
    const field = fieldByKey.get(key);
    const value = typedValue(draft.inputDefaults[key] ?? "", fieldKind(field?.type ?? "string"), field?.label ?? key);
    if (value !== null) inputDefaults[key] = value;
  }
  // #326 (UC-2): dieselbe Prüfung, die auch inline am Feld steht — Typ, „nur enger"
  // (Budget/Aufbewahrung) und die Eskalations-Rolle. Der Master bleibt der Richter; hier
  // wird nur verhindert, dass jemand in sein generisches 400 läuft.
  const settingsOverrides: Record<string, string | number | boolean> = {};
  for (const [key, decl] of Object.entries(spec.settings)) {
    if (!decl.overridable) continue;
    const geprueft = pruefeOverride(key, draft.settingsOverrides[key] ?? "", decl, spec.rollen);
    if (!geprueft.ok) throw new Error(geprueft.fehler);
    if (geprueft.wert !== null) settingsOverrides[key] = geprueft.wert;
  }
  // Review LOW-15: nur die in #300 konfigurierbaren Kinds werden GEBAUT — ein künftiger
  // Kind (mail, #304) wird beim Speichern nicht stillschweigend umgeschrieben.
  let trigger: ConfigureInstancePatch["trigger"];
  if (!draft.triggerKind) trigger = null;
  else if (draft.triggerKind === "manual") trigger = { kind: "manual", config: {} };
  else if (draft.triggerKind === "schedule")
    trigger = {
      kind: "schedule",
      config: {
        cron: draft.cron.trim(),
        timezone: draft.timezone.trim(),
        ...(draft.promptText.trim() ? { promptText: draft.promptText.trim() } : {}),
      },
    };
  else trigger = undefined; // unbekannter Bestands-Kind: unangetastet lassen
  // #330 (UC-1): der Master ersetzt `entscheiderOverrides` VOLL — ohne Berührung fehlt der
  // Schlüssel ganz (kein Phantom-Diff), mit Berührung geht der vollständige Stand raus.
  const entscheiderOverrides = baueEntscheiderPatch(gates, draft.entscheider, gespeichert);
  // #P002 (UC-3): ebenfalls Voll-Ersatz, ebenfalls nur bei netto-Änderung (kein Phantom-Diff).
  const stepModelOverrides = baueStepModelPatch(stepZeilen, draft.stepModels, stepGespeichert);
  return {
    agentId: spec.fixedAgentId ? undefined : draft.agentId.trim() || null,
    trigger,
    inputDefaults,
    settingsOverrides,
    model: draft.model.trim() || null,
    ...(entscheiderOverrides ? { entscheiderOverrides } : {}),
    ...(stepModelOverrides ? { stepModelOverrides } : {}),
  };
}

/**
 * #326 (UC-2): was der Master ablehnen WÜRDE — feldweise, VOR dem Absenden.
 *
 * Schlüssel sind Feld-Adressen (`cron`, `tz`, `i:<casefield>`, `s:<reglerKey>`), damit die
 * Meldung neben dem Feld stehen kann, das sie meint. Ein leeres Ergebnis heisst „absendbar",
 * nicht „richtig" — das Urteil fällt weiterhin der Master.
 */
export function feldFehler(draft: Draft, spec: TemplateSpecView | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!spec) return out;

  if (draft.triggerKind === "schedule") {
    const c = cronFehler(draft.cron);
    if (c) out.cron = c;
    // Review L7: die Wire-Deckel des `triggerSchema` mitprüfen — sonst kommt die Absage
    // erst als generisches „Validation failed" zurück.
    if (draft.timezone.trim() === "") out.tz = "Ohne Zeitzone lässt sich ein Zeitplan nicht einrichten.";
    else if (draft.timezone.trim().length > MAX_TIMEZONE_LAENGE) {
      out.tz = `Die Zeitzone ist länger als ${MAX_TIMEZONE_LAENGE} Zeichen.`;
    }
    if (draft.promptText.length > MAX_PROMPTTEXT_LAENGE) {
      out.prompt = `Der Prompt-Text ist länger als ${MAX_PROMPTTEXT_LAENGE} Zeichen.`;
    }
  }

  const fieldByKey = new Map(spec.casefields.map((f) => [f.key, f] as const));
  for (const key of [...spec.input.required, ...spec.input.optional]) {
    const field = fieldByKey.get(key);
    try {
      typedValue(draft.inputDefaults[key] ?? "", fieldKind(field?.type ?? "string"), field?.label ?? key);
    } catch (err) {
      out[`i:${key}`] = err instanceof Error ? err.message : "Eingabe ungültig";
    }
  }

  for (const [key, decl] of Object.entries(spec.settings)) {
    if (!decl.overridable) continue;
    const geprueft = pruefeOverride(key, draft.settingsOverrides[key] ?? "", decl, spec.rollen);
    if (!geprueft.ok) out[`s:${key}`] = geprueft.fehler;
  }
  return out;
}

export function ProcessInstanceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: instance, isLoading, isError } = useProcessInstance(id);
  const spec = useTemplateSpec(id);
  // #301 (UC-5b): die Läufe DIESER Einrichtung — und zugleich die Zahl, die der
  // Lösch-Dialog nennt. Ein Lauf überlebt seine Instanz; das darf niemanden überraschen.
  const laeufe = useCases({ instanceId: id ?? "", limit: 10 }, { enabled: !!id });
  const template = useProcess(instance?.templateId);
  const agents = useAgents();
  const users = useUsers();
  const configure = useConfigureInstance();
  const pause = usePauseInstance();
  const resume = useResumeInstance();
  const del = useDeleteInstance();
  const provision = useProvisionConnectors();

  const ctx = useUserContext();
  // #299-Vorgänge statt role-Checks: Aktionen nur, wenn der Server den Vorgang erlaubt
  // (Review LOW-13); das Objekt-Urteil fällt weiterhin der Master.
  const darfEdit = darfVorgang(ctx.data, "process-instance.edit");

  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // #330 (Fix-Runde 1, MED-5): die je Freigabe-Schritt GELESENE Mitglieder-Menge. Sie kommt
  // aus den Zeilen der Entscheider-Karte nach oben, weil nur mit ihr der Präventiv-Fehler
  // dieselben Menschen zählt wie der ⊆-Wächter des Masters.
  const [mitgliederProGate, setMitgliederProGate] = useState<Record<string, string[]>>({});
  const meldeMitglieder = useCallback((stepId: string, userIds: string[]) => {
    setMitgliederProGate((alt) => {
      const vorher = alt[stepId];
      // Ohne diesen Vergleich schriebe jeder Render einen neuen State und die Zeile liefe
      // im Kreis (die Liste kommt als neue Referenz aus dem Cache).
      if (vorher && vorher.length === userIds.length && vorher.every((x, i) => x === userIds[i])) return alt;
      return { ...alt, [stepId]: userIds };
    });
  }, []);
  useEffect(() => {
    if (instance) setDraft(draftFrom(instance));
    // #330 (Fix-Runde 2, LOW-5): die gelesenen Mitglieder-Mengen gehören zur EINEN Einrichtung.
    // Beim Wechsel A→B blieben sie sonst für gleichnamige Schritt-Kennungen (`s2`!) stehen,
    // und der Präventiv-Fehler zählte kurz die Menschen der vorigen Einrichtung.
    setMitgliederProGate({});
    // Bewusst NUR die Id (Review NIT-17): ein Reconciler-Sweep ändert updatedAt und würde
    // sonst ungespeicherte Formular-Eingaben zurücksetzen; nach dem eigenen Save setzt
    // onSuccess den Draft explizit auf den Server-Stand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance?.id]);

  // #P001 (UC-2): der Agent, für den die Modell-Liste gilt — eine fixierte Bindung der Vorlage
  // geht der eigenen Wahl vor. Ohne Agent gibt es nichts zu laden (die Query bleibt aus), und
  // ein Agent-Wechsel lädt neu, weil der Key den agentId trägt.
  const modelAgentId = spec.data?.fixedAgentId ?? draft?.agentId ?? "";
  const modelle = useAgentModels(modelAgentId || undefined);

  const userMap = useMemo(() => new Map((users.data ?? []).map((u) => [u.id, u] as const)), [users.data]);

  if (isLoading && !instance) {
    return (
      <div className="space-y-4">
        <BackLink to="/processes" label="Prozesse" />
        <p className="text-[13px] text-text3">Lade…</p>
      </div>
    );
  }
  if (isError || !instance || !draft) {
    return (
      <div className="space-y-4">
        <BackLink to="/processes" label="Prozesse" />
        <p className="text-[13px] text-danger">Instanz nicht gefunden.</p>
      </div>
    );
  }

  const specData = spec.data;
  const allowedKinds = (specData?.triggerKinds ?? []).filter((k) =>
    (CONFIGURABLE_KINDS as readonly string[]).includes(k),
  );
  // #326 (UC-2): was die Vorlage ERLAUBT, hier aber (noch) nicht einrichtbar ist. Vorher
  // stand bei so einer Vorlage ein leeres Auswahlfeld ohne ein Wort dazu.
  const nichtEinrichtbareKinds = (specData?.triggerKinds ?? []).filter(
    (k) => !(CONFIGURABLE_KINDS as readonly string[]).includes(k),
  );
  const inputKeys = specData ? [...specData.input.required, ...specData.input.optional] : [];
  // #326 (UC-2): ALLE deklarierten Regler — die freigegebenen zum Ändern, die übrigen
  // lesbar mit ihrer Herkunft. Ein unsichtbarer Regler sieht aus wie ein nicht gesetzter.
  const alleSettings = Object.entries(specData?.settings ?? {});
  // #P001 (UC-2): die Optionen des Modell-Pickers. `targets` beschreibt GENAU die wählbaren
  // Namen; fehlt es (älterer Master), bleibt die blosse Namensliste — lieber kein Ziel als ein
  // erfundenes. Ein gespeicherter Wert, der nicht mehr darin steht (Slot aus/weg, Whitelist
  // verengt, Ladefehler), bleibt als Option stehen: still verschwinden darf er nie.
  const modelDaten = modelle.data;
  const modelleLaedt = !!modelAgentId && modelle.isLoading;
  // Geteilte Ableitung (Review UC-5 MED-3) — eine Regel für alle drei Picker.
  const modelBasis = modelBasisAus(modelDaten);
  const modelFix = fixLabelAus(modelDaten);
  // ⚠ Review HIGH-1: unter einem Admin-Fix ist `targets` leer, ein GESPEICHERTER Instanz-Wert
  // steht aber noch im Patch — und den lehnt der Master ab („model is fixed by the admin"), womit
  // KEIN anderes Feld der Einrichtung mehr speicherbar wäre. Deshalb bleibt das Feld unter dem
  // Fix genau so weit bedienbar, dass man die wirkungslose Wahl ENTFERNEN kann (leer + Altwert),
  // aber keine neue treffen. Ohne Altwert ist es zu.
  //
  // `belastbar: true` — anders als bei den Agent-Defaults trägt der Marker hier IMMER: an
  // diesem Feld ist er zugleich der Hinweis „diese Wahl greift nicht mehr", und die Bereitschaft
  // (`MODEL_UNRESOLVABLE`) sagt unabhängig von der Ladelage dasselbe.
  const modelBasisImFix = modelFix !== null ? [] : modelBasis;
  const modelOptionen = mitAltwert(modelBasisImFix, draft.model, true);
  const hatAltwert = modelOptionen.length > modelBasisImFix.length;
  const fehler = feldFehler(draft, specData);
  // #330 (UC-1): der Präventiv-Fehler der Entscheider-Wahl sperrt dasselbe „Speichern" —
  // er gehört zu demselben Patch und darf nicht an einem eigenen Knopf hängen.
  // ⚠ Nur über die BERÜHRTEN Schritte (Fix-Runde 1, MED-4): eine gespeicherte Unterbesetzung
  // an einem fremden Schritt darf das Ändern von Cron oder Modell nicht sperren.
  const entscheiderMeldungen = entscheiderFehler(
    instance.gates,
    draft.entscheider,
    // ⚠ Der gespeicherte Stand gehört dazu (#332 Fix-Runde 4, MED-1): sagt eine Berührung
    // dasselbe wie der Bestand, geht nichts raus — dann darf sie auch nichts sperren.
    instance.entscheiderOverrides,
    instance.ownerId,
    mitgliederProGate,
  );
  const hatFeldFehler =
    Object.keys(fehler).length > 0 || Object.keys(entscheiderMeldungen).length > 0;
  // #320 (UC-4): dieselbe Runde stellt Connectors UND Plugins bereit — der Knopf erscheint
  // deshalb bei beiden Lücken-Arten. `PLUGIN_NOT_APPROVED` steht bewusst NICHT dabei: das
  // kann nur ein Admin lösen, ein Knopf dafür wäre eine leere Zusage.
  const behebbareGaps = instance.gaps.filter(
    (g) =>
      g.code === "CONNECTOR_MISSING" ||
      g.code === "CONNECTOR_NOT_CONNECTED" ||
      g.code === "PLUGIN_NOT_ENABLED",
  );

  const save = () => {
    if (!specData) return;
    // #330 (UC-1, #324-L6-Muster): zwei Schlösser. Der Knopf ist gesperrt UND der Handler
    // steigt aus — sonst genügte ein Tastendruck, um an der gesperrten Fläche vorbeizukommen.
    if (hatFeldFehler) return;
    let patch: ConfigureInstancePatch;
    try {
      patch = buildPatch(
        draft,
        specData,
        instance.gates,
        instance.entscheiderOverrides,
        instance.stepModels,
        instance.stepModelOverrides,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Eingabe ungültig");
      return;
    }
    configure.mutate(
      { id: instance.id, patch },
      {
        onSuccess: (updated) => {
          setDraft(draftFrom(updated));
          toast.success(updated.status === "ready" ? "Gespeichert — Instanz ist bereit" : "Gespeichert");
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 400) {
            const body = err.body as { code?: string; error?: string } | undefined;
            // #330 (Fix-Runde 1, MED-3): die SERVER-Meldung durchreichen statt sie durch einen
            // festen Regler-Satz zu ersetzen. Unter `INVALID_INSTANCE_SETTINGS` liegen seit
            // #329 auch Entscheider-Absagen — der alte Text schickte den Nutzer dann zu den
            // Reglern, wo es gar nichts zu beheben gab. Die Meldung ist generisch entworfen
            // (leakt nichts) und wird von React escapt.
            const meldung = typeof body?.error === "string" ? body.error.trim() : "";
            toast.error(
              body?.code === "INVALID_INSTANCE_SETTINGS"
                ? meldung
                  ? `Einstellung nicht erlaubt: ${meldung}`
                  : "Einstellung nicht erlaubt (nur freigegebene Keys, Budget/Retention nur enger)"
                : body?.code === "INVALID_INSTANCE_CONFIG"
                  ? "Konfiguration passt nicht zum Template"
                  : "Eingabe ungültig",
            );
            return;
          }
          toast.error("Speichern fehlgeschlagen");
        },
      },
    );
  };

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo={`/processes/${instance.templateId}`}
        backLabel={template.data?.name ?? instance.templateId}
        icon={Workflow}
        title={`Instanz · ${template.data?.name ?? instance.templateId}`}
        badge={<InstanceStatusBadge status={instance.status} />}
        sub={instance.id}
        actions={
          <div className="flex items-center gap-2">
            {darfEdit && (instance.status === "paused" ? (
              <Button
                variant="outline"
                onClick={() =>
                  resume.mutate(instance.id, {
                    onSuccess: (r) => toast.success(r.status === "ready" ? "Fortgesetzt — bereit" : "Fortgesetzt — noch nicht bereit"),
                    onError: (err) =>
                      toast.error(
                        err instanceof ApiError && err.status === 409
                          ? "Template ist nicht aktiv — Instanz bleibt pausiert"
                          : "Fortsetzen fehlgeschlagen",
                      ),
                  })
                }
                disabled={resume.isPending}
                data-testid="instance-resume"
              >
                <Play className="h-4 w-4" />
                Fortsetzen
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() =>
                  pause.mutate(instance.id, {
                    onSuccess: () => toast.success("Pausiert"),
                    onError: () => toast.error("Pausieren fehlgeschlagen (Entwurf zuerst konfigurieren)"),
                  })
                }
                disabled={pause.isPending}
                data-testid="instance-pause"
              >
                <Pause className="h-4 w-4" />
                Pausieren
              </Button>
            ))}
            {darfEdit && (
              <Button variant="ghost" onClick={() => setDeleteOpen(true)} data-testid="instance-delete">
                <Trash2 className="h-4 w-4" />
                Löschen
              </Button>
            )}
          </div>
        }
      />

      <PropGrid
        rows={[
          [
            "Template",
            <Link key="t" to={`/processes/${instance.templateId}`} className="text-[12.5px] text-accent hover:underline">
              {template.data?.name ?? instance.templateId}
            </Link>,
          ],
          [
            "Inhaber",
            <EntityLink
              key="o"
              to={`/users/${instance.ownerId}`}
              id={instance.ownerId}
              label={userMap.get(instance.ownerId)?.email}
              isResolving={users.isLoading || users.isError}
            />,
          ],
          ["Aktive Version", specData ? <span key="v" className="font-mono text-[12.5px]">{specData.version}</span> : "—"],
          ["Angelegt", new Date(instance.createdAt).toLocaleString("de-CH")],
        ]}
      />

      <SectionCard title="Konfiguration">
        {/* Nur-Leser (R2-NIT): ohne edit-Vorgang sind die Felder gesperrt statt folgenlos tippbar */}
        <fieldset disabled={!darfEdit} className="space-y-4 disabled:opacity-70">
          {specData?.fixedAgentId ? (
            <p className="text-[12.5px] text-text2">
              Agent ist vom Template fixiert:{" "}
              <Link to={`/agents/${specData.fixedAgentId}`} className="font-mono text-accent hover:underline">
                {specData.fixedAgentId}
              </Link>
            </p>
          ) : (
            <div className="w-72 space-y-1.5">
              <Label>Agent</Label>
              <Select value={draft.agentId || "__none"} onValueChange={(v) => setDraft({ ...draft, agentId: v === "__none" ? "" : v })} disabled={!darfEdit}>
                <SelectTrigger data-testid="instance-agent-select">
                  <SelectValue placeholder="Agent wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— kein Agent —</SelectItem>
                  {(agents.data ?? []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} ({a.id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-56 space-y-1.5">
              <Label>Auslöser</Label>
              <Select
                value={draft.triggerKind || "__none"}
                onValueChange={(v) => setDraft({ ...draft, triggerKind: v === "__none" ? "" : v })}
                disabled={!darfEdit}
              >
                <SelectTrigger data-testid="instance-trigger-select">
                  <SelectValue placeholder="Auslöser wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— nicht konfiguriert —</SelectItem>
                  {allowedKinds.map((k) => (
                    <SelectItem key={k} value={k}>
                      {ausloeserLabel(k)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {draft.triggerKind === "schedule" && (
              <>
                <div className="w-44 space-y-1.5">
                  <Label>Zeitplan (5 Felder)</Label>
                  <Input
                    value={draft.cron}
                    onChange={(e) => setDraft({ ...draft, cron: e.target.value })}
                    placeholder="0 8 * * 1"
                    data-testid="instance-cron"
                  />
                  {fehler.cron && (
                    <p className="text-[12px] text-danger" data-testid="instance-cron-fehler">
                      {fehler.cron}
                    </p>
                  )}
                </div>
                <div className="w-44 space-y-1.5">
                  <Label>Zeitzone</Label>
                  <Input
                    value={draft.timezone}
                    onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
                    data-testid="instance-timezone"
                  />
                  {fehler.tz && <p className="text-[12px] text-danger">{fehler.tz}</p>}
                </div>
                <div className="w-72 space-y-1.5">
                  <Label>Prompt-Text (optional)</Label>
                  <Input
                    value={draft.promptText}
                    onChange={(e) => setDraft({ ...draft, promptText: e.target.value })}
                    placeholder="Kontext für den Lauf"
                    data-testid="instance-prompttext"
                  />
                  {fehler.prompt && <p className="text-[12px] text-danger">{fehler.prompt}</p>}
                </div>
              </>
            )}
            <div className="w-72 space-y-1.5">
              <Label>Modell (optional)</Label>
              <select
                value={draft.model}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}
                disabled={!darfEdit || !modelAgentId || modelleLaedt || (modelFix !== null && !hatAltwert)}
                className={INPUT_CLASS}
                data-testid="instance-model"
              >
                <option value="">{modelleLaedt ? "Modelle werden geladen…" : "Agent-Standard"}</option>
                {modelOptionen.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {!modelAgentId && (
                <p className="text-[12px] text-text3" data-testid="instance-model-kein-agent">
                  Erst einen Agenten binden — welche Modelle wählbar sind, hängt an ihm.
                </p>
              )}
              {modelFix !== null && (
                <p className="text-[12px] text-text3" data-testid="instance-model-fix">
                  Admin-Fix: {modelFix} — der Admin hat das Modell fixiert, die Einrichtung kann nicht
                  abweichen.{hatAltwert ? " Die gespeicherte Wahl ist wirkungslos und blockiert das Speichern — auf „Agent-Standard“ stellen." : ""}
                </p>
              )}
              {modelAgentId && modelle.isError && (
                <p className="text-[12px] text-danger" data-testid="instance-model-fehler">
                  Modell-Liste nicht abrufbar.{" "}
                  <button type="button" className="underline" onClick={() => void modelle.refetch()}>
                    Erneut laden
                  </button>
                </p>
              )}
            </div>
          </div>

          {/* #326 (UC-2): der Rahmen der Vorlage wird BENANNT — sonst steht bei einer
              Vorlage, die nur E-Mail/Webhook erlaubt, ein leeres Auswahlfeld ohne Grund. */}
          {specData && allowedKinds.length === 0 && (
            <p className="text-[12.5px] text-warn" data-testid="instance-trigger-rahmen-leer">
              Diese Vorlage erlaubt nur Auslöser, die sich hier noch nicht einrichten lassen
              {nichtEinrichtbareKinds.length > 0 ? ` (${nichtEinrichtbareKinds.map(ausloeserLabel).join(", ")})` : ""}.
              Der Lauf muss so lange von Hand angestossen werden.
            </p>
          )}
          {specData && allowedKinds.length > 0 && nichtEinrichtbareKinds.length > 0 && (
            <p className="text-[12px] text-text3" data-testid="instance-trigger-rahmen">
              Die Vorlage erlaubt ausserdem {nichtEinrichtbareKinds.map(ausloeserLabel).join(", ")} — diese
              Auslöser lassen sich hier noch nicht einrichten.
            </p>
          )}

          {inputKeys.length > 0 && (
            <div className="space-y-2">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-text3">Input-Vorbelegungen</div>
              <div className="flex flex-wrap gap-3">
                {inputKeys.map((key) => {
                  const field = specData?.casefields.find((f) => f.key === key);
                  return (
                    <div key={key} className="w-56 space-y-1.5">
                      <Label>
                        {field?.label ?? key}
                        {specData?.input.required.includes(key) ? " *" : ""}
                      </Label>
                      {field?.choices ? (
                        <Select
                          value={draft.inputDefaults[key] || "__none"}
                          onValueChange={(v) =>
                            setDraft({ ...draft, inputDefaults: { ...draft.inputDefaults, [key]: v === "__none" ? "" : v } })
                          }
                          disabled={!darfEdit}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">—</SelectItem>
                            {field.choices.map((c) => (
                              <SelectItem key={c} value={c}>
                                {c}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={draft.inputDefaults[key] ?? ""}
                          onChange={(e) =>
                            setDraft({ ...draft, inputDefaults: { ...draft.inputDefaults, [key]: e.target.value } })
                          }
                          placeholder={field?.type ?? "string"}
                        />
                      )}
                      {fehler[`i:${key}`] && (
                        <p className="text-[12px] text-danger" data-testid={`instance-input-fehler-${key}`}>
                          {fehler[`i:${key}`]}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* #326 (UC-2): die Regler-Maske. Vorher waren nur die freigegebenen sichtbar —
              ein gesperrter Regler sah damit aus wie ein nicht gesetzter, und ein gesetzter
              Override war von einem geerbten Wert nicht zu unterscheiden. Jetzt steht an
              jeder Zeile, WOHER der geltende Wert kommt und ob er hier änderbar ist. */}
          {alleSettings.length > 0 && (
            <div className="space-y-2" data-testid="instance-settings">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-text3">
                Regler der Vorlage
              </div>
              <div className="space-y-2.5">
                {alleSettings.map(([key, decl]) => {
                  const roh = draft.settingsOverrides[key] ?? "";
                  const gesetzt = roh.trim() !== "";
                  const meldung = fehler[`s:${key}`];
                  return (
                    <div key={key} className="flex flex-wrap items-center gap-2.5" data-testid={`instance-regler-${key}`}>
                      <span className="w-64 text-[12.5px]">
                        {reglerLabel(key)}
                        {!decl.overridable && (
                          <span className="ml-2 text-[11px] text-text3" data-testid={`instance-regler-${key}-herkunft`}>
                            Vorlage · nicht änderbar
                          </span>
                        )}
                      </span>
                      {decl.overridable ? (
                        <>
                          {key === "escalationRole" && typeof decl.value === "string" ? (
                            <select
                              className="h-9 w-56 rounded-md border border-border bg-surface px-2 text-[13px]"
                              value={roh}
                              onChange={(e) =>
                                setDraft({ ...draft, settingsOverrides: { ...draft.settingsOverrides, [key]: e.target.value } })
                              }
                              disabled={!darfEdit}
                              data-testid={`instance-regler-${key}-wert`}
                            >
                              <option value="">Vorlage: {String(decl.value)}</option>
                              {(specData?.rollen ?? []).map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                              {/* #326 (Review L3): ein Bestands-Override auf eine Rolle, die
                                  die Vorlage nicht (mehr) deklariert, bleibt sichtbar — sonst
                                  fiele das Feld auf „Vorlage" zurück und der nächste Klick auf
                                  Speichern verwürfe still eine bestehende Einstellung. Die
                                  Prüfung meldet sie daneben als unzulässig (wie in der
                                  Vorlagen-Maske). */}
                              {roh !== "" && !(specData?.rollen ?? []).includes(roh) && (
                                <option value={roh}>{roh} (nicht deklariert)</option>
                              )}
                            </select>
                          ) : (
                            <Input
                              className="w-56"
                              value={roh}
                              onChange={(e) =>
                                setDraft({ ...draft, settingsOverrides: { ...draft.settingsOverrides, [key]: e.target.value } })
                              }
                              placeholder={`Vorlage: ${String(decl.value)}`}
                              data-testid={`instance-regler-${key}-wert`}
                            />
                          )}
                          {gesetzt ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setDraft({ ...draft, settingsOverrides: { ...draft.settingsOverrides, [key]: "" } })
                              }
                              disabled={!darfEdit}
                              data-testid={`instance-regler-${key}-zuruecksetzen`}
                            >
                              auf Vorlage zurück
                            </Button>
                          ) : (
                            <span className="text-[11.5px] text-text3">Vorlage gilt</span>
                          )}
                        </>
                      ) : (
                        <span className="text-[12.5px] font-mono" data-testid={`instance-regler-${key}-fest`}>
                          {String(decl.value)}
                        </span>
                      )}
                      {meldung && (
                        <span className="basis-full text-[12px] text-danger" data-testid={`instance-regler-${key}-fehler`}>
                          {meldung}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[12px] text-text3">
                Leeres Feld heisst: es gilt der Wert der Vorlage. Kostendeckel und Aufbewahrung
                lassen sich nur verkürzen, nie verlängern.
              </p>
            </div>
          )}

          {darfEdit && (
            <div className="flex items-center gap-3">
              <Button
                onClick={save}
                disabled={configure.isPending || !specData || hatFeldFehler}
                data-testid="instance-save"
              >
                <Save className="h-4 w-4" />
                {configure.isPending ? "Speichere…" : "Speichern"}
              </Button>
              {hatFeldFehler && (
                <span className="text-[12px] text-danger" data-testid="instance-save-gesperrt">
                  Erst die rot markierten Stellen beheben.
                </span>
              )}
            </div>
          )}
        </fieldset>
      </SectionCard>

      {/* #330 (UC-1): wen ein Freigabe-Schritt fragt. Eigene Karte, aber DERSELBE
          Speicherweg wie die Konfiguration darüber — es ist ein Patch an derselben
          Einrichtung, und zwei Speichern-Knöpfe nebeneinander wären zwei Wahrheiten. */}
      {/* #P002 (UC-3): welches Modell einen Schritt fährt — eigene Karte, DERSELBE
          Speicherweg wie die Konfiguration darüber (ein Patch an derselben Einrichtung). */}
      <StepModelCard
        instance={instance}
        beruehrt={draft.stepModels}
        onChange={(stepId, wert) => setDraft({ ...draft, stepModels: { ...draft.stepModels, [stepId]: wert } })}
        darfEdit={darfEdit}
      />

      <EntscheiderCard
        instance={instance}
        beruehrt={draft.entscheider}
        onChange={(stepId, wahl) => setDraft({ ...draft, entscheider: { ...draft.entscheider, [stepId]: wahl } })}
        fehler={entscheiderMeldungen}
        onMitglieder={meldeMitglieder}
        darfEdit={darfEdit}
      />

      {/* #320 (UC-2): die Plugin-Vorgabe des Prozesses — hier für diese Einrichtung
          anpassbar. Steht bewusst NEBEN der Konfiguration (eigene Karte, eigener
          Speicherweg): die Liste ist eine Auswahl aus dem Katalog, kein Formularfeld. */}
      <RequiredPluginsCard
        instance={instance}
        // #320 (Review L3): der EFFEKTIVE Agent — fixierter aus der Prozess-Vorgabe schlägt
        // den gebundenen (dieselbe Auflösung wie im Master). Er bestimmt, WELCHEN Katalog
        // das Auswahlfeld zeigt.
        agentId={specData?.fixedAgentId ?? instance.agentId}
        darfEdit={darfEdit}
      />

      <SectionCard title="Bereitschaft">
        {instance.status === "ready" ? (
          <div>
            <div className="flex items-center gap-2 py-1.5 text-[12.5px] font-semibold text-ok">
              <CheckCircle2 className="h-4 w-4" /> Bereit — der Auslöser wird mit der Lauf-Engine scharf.
            </div>
            {instance.gaps.length > 0 && (
              <div className="divide-y divide-border">
                {instance.gaps.map((g, i) => (
                  <GapRow key={i} gap={g} />
                ))}
              </div>
            )}
          </div>
        ) : instance.gaps.length > 0 ? (
          <div className="space-y-3">
            <div className="divide-y divide-border">
              {instance.gaps.map((g, i) => (
                <GapRow key={i} gap={g} />
              ))}
            </div>
            {darfEdit && behebbareGaps.length > 0 && (
              <Button
                variant="outline"
                onClick={() =>
                  provision.mutate(instance.id, {
                    onSuccess: (r) => {
                      // #320: Plugin-Ergebnisse gleichberechtigt melden. `plugins` fehlt
                      // bei einem älteren Master ganz — dann wird nichts erfunden.
                      // ⚠ Review L2: `already` („war schon zugewiesen") zählt in KEINE der
                      // drei Listen — sonst meldete der Toast eine Aktivierung, die nicht
                      // stattgefunden hat. Waren alle schon da, greift „Nichts zu aktivieren".
                      const plugins = r.plugins ?? [];
                      const namen = (art: string) =>
                        plugins.filter((p) => p.result === art).map((p) => p.pluginName);
                      const aktiviert = [...r.provisioned, ...namen("enabled")];
                      const freigabe = [...r.approvalRequired, ...namen("approvalRequired")];
                      const misslungen = [...(r.failed ?? []), ...namen("failed")];
                      if (aktiviert.length > 0) toast.success(`Aktiviert: ${aktiviert.join(", ")}`);
                      if (freigabe.length > 0) toast.info(`Freigabe nötig: ${freigabe.join(", ")}`);
                      if (misslungen.length > 0) toast.error(`Nicht aktivierbar: ${misslungen.join(", ")}`);
                      if (aktiviert.length === 0 && freigabe.length === 0 && misslungen.length === 0)
                        toast.info("Nichts zu aktivieren");
                    },
                    onError: () => toast.error("Bereitstellung fehlgeschlagen"),
                  })
                }
                disabled={provision.isPending}
                data-testid="instance-provision"
              >
                <Plug className="h-4 w-4" />
                {provision.isPending ? "Stelle bereit…" : "Fehlendes aktivieren"}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-[12.5px] text-text3">
            Noch nicht geprüft — Speichern stößt die Prüfung an; pausierte Instanzen prüft erst „Fortsetzen".
          </p>
        )}
        <p className="mt-3 text-[12px] leading-relaxed text-text3">
          Die Bereitschaft wird bei jeder Änderung, bei Template-Updates und periodisch neu geprüft.
        </p>
      </SectionCard>

      {/* #303 (UC-2): der manuelle Start. Nur bei Auslöser „manuell" und nur an einer
          bereiten Einrichtung — das Urteil fällt der Master, die Oberfläche zeigt den
          Knopf bloss dort, wo er Sinn ergibt. */}
      {darfEdit && instance.status === "ready" && instance.trigger?.kind === "manual" && (
        <ManualStartCard instance={instance} spec={specData} />
      )}

      {/* #301 (UC-5b): die Läufe dieser Einrichtung. Vorher endete die Instanz-Sicht bei der
          Konfiguration — was daraus tatsächlich geworden ist, stand nirgends.
          #303 (UC-2): mit den Spalten, nach denen jemand tatsächlich fragt — wo steht er,
          wann lief er, was hat er gekostet, wer hat ihn angestossen. */}
      <SectionCard title="Läufe" noPad>
        {laeufe.isError ? (
          <p className="px-[18px] py-4 text-[12.5px] text-danger">Läufe nicht abrufbar.</p>
        ) : (laeufe.data?.cases ?? []).length === 0 ? (
          <p className="px-[18px] py-4 text-[12.5px] text-text3">Noch kein Lauf gestartet.</p>
        ) : (
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zustand</TableHead>
                  <TableHead>Fortschritt</TableHead>
                  <TableHead>Aktueller Schritt</TableHead>
                  <TableHead>Gestartet</TableHead>
                  <TableHead>Beendet</TableHead>
                  <TableHead className="text-right">Kosten</TableHead>
                  <TableHead>Auslöser</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(laeufe.data?.cases ?? []).map((c) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/cases/${c.id}`)}
                    data-testid="instance-case-row"
                  >
                    <TableCell>
                      <CaseStatusBadge status={c.status} />
                    </TableCell>
                    <TableCell>
                      <StepProgress steps={c.steps} currentStepId={c.currentStepId} />
                    </TableCell>
                    {/* Klarname wo vorhanden, sonst die Id — `currentStep` ist Beiwerk und
                        fehlt bei einem älteren Master ganz (dann bleibt `currentStepId`). */}
                    <TableCell className="text-[12px] text-text2">
                      {c.currentStep?.name ?? c.currentStep?.id ?? c.currentStepId ?? "—"}
                    </TableCell>
                    <TableCell className="text-[12px] text-text2">{relativeTime(c.createdAt)}</TableCell>
                    <TableCell className="text-[12px] text-text2">
                      {c.finishedAt ? relativeTime(c.finishedAt) : "—"}
                    </TableCell>
                    {/* `null`/fehlend heisst „keine Sitzung auffindbar" — NICHT „$0.00". */}
                    <TableCell className="text-right text-[12px]">
                      {typeof c.costUsd === "number" ? (
                        <span className={costClassName(c.costUsd)}>{formatUsd(c.costUsd)}</span>
                      ) : (
                        <span className="text-text3">–</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[12px] text-text2">
                      {c.triggerKind}
                      {c.dryRun && <span className="ml-1.5 text-text3">(Probelauf)</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {(laeufe.data?.total ?? 0) > (laeufe.data?.cases ?? []).length && (
              <div className="px-[18px] py-2">
                <Link
                  to={`/cases?instanceId=${encodeURIComponent(instance.id)}`}
                  className="text-[12.5px] text-accent hover:underline"
                >
                  alle {laeufe.data?.total} Läufe ansehen →
                </Link>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Instanz löschen?"
        description={
          // #301 (UC-5b): der Dialog sagt, was BLEIBT. Läufe hängen nicht mehr an ihrer
          // Instanz — wer das nicht liest, wundert sich später, warum die Historie noch da ist.
          <>
            Entfernt die Einrichtung (Agent-Bindung, Auslöser, Vorbelegungen). Das Template bleibt
            bestehen.
            {(laeufe.data?.total ?? 0) > 0 && (
              <>
                {" "}
                <strong>
                  {laeufe.data?.total} bisherige {laeufe.data?.total === 1 ? "Lauf bleibt" : "Läufe bleiben"} erhalten
                </strong>{" "}
                und weiter einsehbar.
              </>
            )}
          </>
        }
        confirmText="Löschen"
        confirmVariant="destructive"
        onConfirm={() =>
          del.mutate(instance.id, {
            onSuccess: () => {
              toast.success("Instanz gelöscht");
              navigate(`/processes/${instance.templateId}`);
            },
            onError: (err) =>
              toast.error(
                err instanceof ApiError && err.status === 409
                  ? "Es laufen noch Vorgänge — diese zuerst abbrechen oder abwarten"
                  : "Löschen fehlgeschlagen",
              ),
          })
        }
      />
    </div>
  );
}
